/*
 * DateRangePicker — shared util. Trigger button + portal popover wrapping
 * react-date-range's DateRangePicker: presets sidebar, two-month calendar, and a
 * footer showing the draft range with Cancel / Apply. Selection is draft-then-commit:
 * calendar clicks and preset clicks update a local draft; only Apply calls onApply.
 *
 * Consolidates the copy-pasted pattern from views/reports/SmsReportDateRange,
 * views/twilio_agent/RecentCallsFilters, etc. Themed via CSS variables so light
 * and dark both work (see date_range_picker.css).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaRegCalendarAlt, FaChevronDown } from 'react-icons/fa';
import { DateRangePicker as RdrDateRangePicker, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  PRESETS,
  tillDatePreset,
  formatTriggerLabel,
  formatTriggerLabelShort,
  formatFooter,
  toApiDate
} from './utils';
import './date_range_picker.css';

// Parse a YYYY-MM-DD string as a local date (avoids the UTC shift of new Date('YYYY-MM-DD')).
const toDate = (value) => (value ? new Date(`${value}T00:00:00`) : new Date());

function DateRangePicker({
  startDate,
  endDate,
  onApply,
  minDate,
  maxDate,
  months,
  align,
  tillDateStart,
  compact
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [draft, setDraft] = useState({ startDate: toDate(startDate), endDate: toDate(endDate) });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const staticRanges = useMemo(
    () => createStaticRanges([...PRESETS, tillDatePreset(tillDateStart)]),
    [tillDateStart]
  );
  // Historical reports shouldn't allow future dates; default the cap to today.
  const effectiveMax = maxDate ? toDate(maxDate) : new Date();

  // Position the portal popover under the trigger; close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: align === 'right' ? window.innerWidth - rect.right : rect.left
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, align]);

  const openPicker = () => {
    setDraft({ startDate: toDate(startDate), endDate: toDate(endDate) });
    setOpen(true);
  };

  const handleSelect = (item) => {
    setDraft({ startDate: item.selection.startDate, endDate: item.selection.endDate });
  };

  const apply = () => {
    onApply({ startDate: toApiDate(draft.startDate), endDate: toApiDate(draft.endDate) });
    setOpen(false);
  };

  const cancel = () => {
    setDraft({ startDate: toDate(startDate), endDate: toDate(endDate) });
    setOpen(false);
  };

  const ranges = [{ startDate: draft.startDate, endDate: draft.endDate, key: 'selection' }];
  const rightAligned = align === 'right';

  return (
    <div ref={rootRef} className="udr-root">
      <button
        ref={triggerRef}
        type="button"
        className={`udr-trigger${compact ? ' udr-trigger--compact' : ''}`}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        <FaRegCalendarAlt size={13} className="udr-trigger-icon" />
        <span>
          {compact
            ? formatTriggerLabelShort(startDate, endDate)
            : formatTriggerLabel(startDate, endDate)}
        </span>
        {compact ? null : <FaChevronDown size={11} className="udr-trigger-icon" />}
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="udr-scope udr-popover"
              style={{
                top: coords.top,
                left: rightAligned ? undefined : coords.left,
                right: rightAligned ? coords.left : undefined
              }}
            >
              <RdrDateRangePicker
                ranges={ranges}
                onChange={handleSelect}
                moveRangeOnFirstSelection={false}
                showDateDisplay={false}
                months={months}
                direction="horizontal"
                inputRanges={[]}
                staticRanges={staticRanges}
                rangeColors={['#ee731b']}
                minDate={minDate ? toDate(minDate) : undefined}
                maxDate={effectiveMax}
              />
              <div className="udr-footer">
                <span className="udr-footer-range">
                  {formatFooter(toApiDate(draft.startDate), toApiDate(draft.endDate))}
                </span>
                <div className="udr-footer-actions">
                  <button type="button" className="udr-cancel" onClick={cancel}>
                    Cancel
                  </button>
                  <button type="button" className="udr-apply" onClick={apply}>
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

DateRangePicker.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string, // '' => defaults to today (no future selection)
  months: PropTypes.number,
  align: PropTypes.oneOf(['left', 'right']),
  tillDateStart: PropTypes.string, // "Till date" preset start; '' => start of current year
  compact: PropTypes.bool // pill trigger: short 'MMM D - MMM D' label, no chevron
};

DateRangePicker.defaultProps = {
  startDate: '',
  endDate: '',
  minDate: '',
  maxDate: '',
  months: 2,
  align: 'left',
  tillDateStart: '',
  compact: false
};

export default DateRangePicker;
